import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import CampaignManager from './components/Campaign/CampaignManager';
import AdvertisementManager from './components/Advertisement/AdvertisementManager';
import { genWeek } from './components/Campaign/CoverageHeatmap';
import CampaignBuilder from './components/Needs/CampaignBuilder';
import CenterVisuals, { RANGE_LABELS as NEEDS_RANGE_LABELS, getRangeWeeks as NEEDS_GET_RANGE_WEEKS } from './components/Needs/CenterVisuals';
import ReviewPanel from './components/Review/ReviewPanel';
import DataInspector from './components/Data/DataInspector';
import { useOverrideVersion } from './state/dataOverrides';
import { getStateSnapshot, setPlanner, useCampaignPlanVersion } from './state/campaignPlan';
import { computeCoveragePercentage } from './services/coverageModel';
import ZonesController from './components/Plan/ZonesController';
import AdSourcesPanel from './components/Sources/AdSourcesPanel';
import DataOverview from './components/Data/DataOverview';

type Tab = 'needs' | 'campaign' | 'advertisement' | 'review' | 'data';

// Job base colors (matching CoverageHeatmap)
const JOB_BASE_COLORS: Record<string, string> = {
  "Server": "#D72A4D",       // Red
  "Cook": "#FB8331",         // Orange
  "Bartender": "#FFCB03",    // Yellow
  "Security": "#21BF6B",     // Green
  "Dishwasher": "#12B9B1",   // Teal
  "Manager": "#2E98DB",      // Light Blue
  "Cleaner": "#3967D6",      // Dark Blue
  "Barista": "#8855D0"       // Purple
};

const AVAILABLE_LOCATIONS = ['BOS', 'LGA', 'DCA', 'ORD'];
const AVAILABLE_JOBS = ['Server', 'Cook', 'Bartender', 'Security', 'Dishwasher', 'Manager', 'Cleaner', 'Barista'];

const JOB_REQUIREMENT_BASE: Record<string, number> = {
  "Server": 8,
  "Cook": 10,
  "Bartender": 6,
  "Security": 4,
  "Dishwasher": 7,
  "Manager": 3,
  "Cleaner": 5,
  "Barista": 6
};

const HALF_HOURS_PER_DAY = 48;

function calculateCoverage(job: string, weeks: number, withCampaign = false, campaignForRole?: string): number {
  return computeCoveragePercentage(job, weeks, { withCampaign, campaignForRole });
}

interface JobFormData {
  role: string;
  completed: boolean;
  data: any; // Store form data for each job
}

// Valid tabs for the recruiting app
const VALID_TABS_FILTERED: Tab[] = ['needs', 'campaign', 'advertisement', 'review', 'data'];

export default function PasscomRecruitingApp() {
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    // Priority: URL param > localStorage > default
    const urlParams = new URLSearchParams(window.location.search);
    const urlTab = urlParams.get('tab');
    
    // Map URL-friendly names to internal tab IDs
    const tabMap: Record<string, Tab> = {
      'plan': 'needs',
      'campaign': 'campaign',
      'posting': 'advertisement',
      'sources': 'review',
      'data': 'data'
    };
    
    if (urlTab && tabMap[urlTab]) {
      return tabMap[urlTab];
    }
    
    // Fall back to localStorage
    const saved = localStorage.getItem('passcom-recruiting-active-tab');
    return (VALID_TABS_FILTERED.includes(saved as Tab) ? (saved as Tab) : 'campaign');
  });

  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [jobForms, setJobForms] = useState<JobFormData[]>([]);
  const [needsRangeIdx, setNeedsRangeIdx] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('passcom-needs-range-idx');
      if (saved) {
        const parsed = Number(saved);
        if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 3) {
          return parsed;
        }
      }
    } catch {}
    return 1; // Default to "Quarter"
  });
  const [dataRangeIdx, setDataRangeIdx] = useState<number>(1);
  const [dataInspectorJob, setDataInspectorJob] = useState<string>(AVAILABLE_JOBS[0]);
  const overrideVersion = useOverrideVersion();
  const planVersion = useCampaignPlanVersion();
  
  // Persist needsRangeIdx to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('passcom-needs-range-idx', String(needsRangeIdx));
    } catch {}
  }, [needsRangeIdx]);
  
  // Stabilize range change callback (empty deps to prevent recreation)
  const handleNeedsRangeChange = useCallback((idx: number) => {
    setNeedsRangeIdx(idx);
  }, []); // ✅ Empty deps - callback never recreated
  
  // ---- Plan Zones (coverage color thresholds) ----
  type Zones = { lowRed: number; lowYellow: number; highYellow: number; highRed: number };
  const DEFAULT_ZONES: Zones = { lowRed: 70, lowYellow: 95, highYellow: 120, highRed: 150 };
  const [zones, setZones] = useState<Zones>(() => {
    try {
      const raw = localStorage.getItem('passcom-plan-zones');
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_ZONES;
  });
  useEffect(() => {
    try { localStorage.setItem('passcom-plan-zones', JSON.stringify(zones)); } catch {}
  }, [zones]);
  const zoneTextColor = (coverage: number, z: Zones) => {
    if (coverage <= z.lowRed) return '#dc2626';        // red
    if (coverage < z.lowYellow) return '#ca8a04';      // yellow (82–95)
    if (coverage <= z.highYellow) return '#16a34a';    // green (95–120)
    if (coverage <= z.highRed) return '#ca8a04';       // yellow (120–140)
    return '#dc2626';                                  // red (>=140)
  };

  // Dropdown states
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const jobDropdownRef = useRef<HTMLDivElement>(null);
  
  // Default Locations: select "All" on first mount if none selected
  const didInitLocations = useRef(false);
  useEffect(() => {
    if (!didInitLocations.current) {
      didInitLocations.current = true;
      if (selectedLocations.length === 0 && AVAILABLE_LOCATIONS.length > 0) {
        setSelectedLocations([...AVAILABLE_LOCATIONS]);
      }
    }
  }, []);
  
  // Ensure a default selected job if none is chosen
  useEffect(() => {
    if (selectedJobs.length === 0 && AVAILABLE_JOBS.length > 0) {
      setSelectedJobs([AVAILABLE_JOBS[0]]);
    }
  }, [selectedJobs]);
  
  // Update job forms when selected jobs change
  useEffect(() => {
    setJobForms(prev => {
      const newForms = selectedJobs.map(role => {
        const existing = prev.find(f => f.role === role);
        return existing || { role, completed: false, data: {} };
      });
      return newForms;
    });
  }, [selectedJobs]);

  useEffect(() => {
    if (selectedJobs.length > 0) {
      setDataInspectorJob(selectedJobs[0]);
    }
  }, [selectedJobs]);

  // Persist tab selection and sync to URL
  useEffect(() => {
    localStorage.setItem('passcom-recruiting-active-tab', activeTab);
    
    // Map internal tab IDs to URL-friendly names
    const tabToUrlMap: Record<Tab, string> = {
      'needs': 'plan',
      'campaign': 'campaign',
      'advertisement': 'posting',
      'review': 'sources',
      'data': 'data'
    };
    
    const urlTab = tabToUrlMap[activeTab];
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('tab', urlTab);
    window.history.replaceState({}, '', currentUrl.toString());
  }, [activeTab]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlTab = urlParams.get('tab');
      
      const tabMap: Record<string, Tab> = {
        'plan': 'needs',
        'campaign': 'campaign',
        'posting': 'advertisement',
        'sources': 'review',
        'data': 'data'
      };
      
      if (urlTab && tabMap[urlTab]) {
        setActiveTab(tabMap[urlTab]);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(event.target as Node)) {
        setShowJobDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ensure planner has a valid start date to avoid invalid Date errors in derived calculations
  useEffect(() => {
    const s = getStateSnapshot();
    if (!s.planner.startDate || isNaN(new Date(s.planner.startDate as any).getTime())) {
      try {
        const todayISO = new Date().toISOString().slice(0, 10);
        setPendingStart();
        function setPendingStart() {
          try {
            setTimeout(() => setPlanner({ startDate: todayISO }), 0);
          } catch {}
        }
      } catch {}
    }
  }, []);

  // ✅ Sources are now initialized synchronously in campaignPlan.ts state creation
  // No longer need async useEffect for source initialization

  const toggleLocationSelection = (location: string) => {
    setSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(loc => loc !== location)
        : [...prev, location]
    );
  };
  const toggleAllLocations = () => {
    setSelectedLocations(prev =>
      prev.length === AVAILABLE_LOCATIONS.length ? [] : [...AVAILABLE_LOCATIONS]
    );
  };

  const selectJob = (job: string) => {
    setSelectedJobs([job]);
    setShowJobDropdown(false);
  };

  const activeJob = selectedJobs[0] || AVAILABLE_JOBS[0];
  
  // Calculate coverage for all jobs
  // Campaign overlay only affects the currently selected job when liveView is enabled
  const jobCoverageData = useMemo(() => {
    const weeks = NEEDS_GET_RANGE_WEEKS(needsRangeIdx);
    const state = getStateSnapshot();
    const liveViewEnabled = !!state.liveView;
    
    return AVAILABLE_JOBS.map(job => ({
      job,
      color: JOB_BASE_COLORS[job] || '#2563eb',
      // Pass activeJob as campaignForRole so only that job gets the campaign overlay
      coverage: calculateCoverage(job, weeks, liveViewEnabled, liveViewEnabled ? activeJob : undefined)
    }));
  }, [needsRangeIdx, overrideVersion, planVersion, activeJob]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'needs', label: 'Plan/Launch' },
    { id: 'campaign', label: 'Campaign' },
    { id: 'advertisement', label: 'Posting' },
    { id: 'review', label: 'Sources' },
    { id: 'data', label: 'Data' }
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <div className="flex-1 overflow-auto">
        {activeTab === 'needs' && (
          <div className="h-full bg-gray-50">
            <div className="h-full max-w-7xl mx-auto px-6 py-8">
              {/* Compact Jobs Icons Bar */}
              <section id="tour-roles" className="mb-2 -mx-6 px-6">
                <JobIconsBar
                  jobs={AVAILABLE_JOBS}
                  colors={JOB_BASE_COLORS}
                  coverageData={jobCoverageData}
                  selectedJob={selectedJobs[0]}
                  onSelect={(job) => setSelectedJobs([job])}
                  zones={zones}
                />
              </section>
              <div className="h-full grid gap-6 grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)]">
                <div className="flex flex-col gap-6">
                  <section className="bg-white border rounded-xl shadow-sm p-0 overflow-hidden">
                    <CampaignBuilder 
                      onOpenSources={() => setActiveTab('review')} 
                      zones={zones}
                      onZonesChange={setZones}
                    />
                  </section>
                  <section className="bg-white border rounded-xl shadow-sm p-4 overflow-y-auto">
                    <div className="flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Zones</h2>
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setZones({ lowRed: 70, lowYellow: 95, highYellow: 120, highRed: 150 })}
                        type="button"
                      >
                        reset
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Warning zones for over/under-staffing.</p>
                    <ZonesController zones={zones} onChange={setZones} />
                  </section>
                </div>
                <section className="bg-white border rounded-xl shadow-sm p-4 overflow-auto">
                  <CenterVisuals
                    job={selectedJobs[0] || AVAILABLE_JOBS[0]}
                    rangeIdx={needsRangeIdx}
                    onRangeChange={handleNeedsRangeChange}
                    zones={zones}
                  />
                </section>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'campaign' && (
          <div className="h-full overflow-auto">
            <CampaignManager
              selectedLocations={selectedLocations}
              setSelectedLocations={setSelectedLocations}
              selectedJobs={selectedJobs}
              setSelectedJobs={setSelectedJobs}
              onOpenPlanner={() => setActiveTab('needs')}
              onOpenSources={() => setActiveTab('review')}
            />
          </div>
        )}
        
        {activeTab === 'advertisement' && (
          <div className="h-full overflow-auto bg-gray-50">
            <AdvertisementManager
              selectedJobs={selectedJobs}
              selectedLocations={selectedLocations}
              jobForms={jobForms}
              setJobForms={setJobForms}
            />
          </div>
        )}
        
        {activeTab === 'review' && (
          <div className="h-full bg-gray-50 overflow-auto">
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
              <div className="bg-white border rounded-xl shadow-sm">
                <ReviewPanel selectedJobs={selectedJobs} selectedLocations={selectedLocations} />
              </div>
              <AdSourcesPanel />
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="h-full bg-gray-50 overflow-auto">
            <div className="h-full max-w-7xl mx-auto px-6 py-8 space-y-6">
              {/* Full-width overview graph */}
              <section className="bg-white border rounded-xl shadow-sm p-4">
                <div className="mb-3 flex items-center justify-end">
                  <label className="text-xs text-gray-600 mr-2">Range</label>
                  <select
                    value={dataRangeIdx}
                    onChange={(e) => setDataRangeIdx(Number(e.target.value))}
                    className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 focus:outline-none focus:border-blue-500"
                  >
                    {NEEDS_RANGE_LABELS.map((label, idx) => (
                      <option key={label} value={idx}>{label}</option>
                    ))}
                  </select>
                </div>
                <DataOverview
                  job={dataInspectorJob}
                    weeks={NEEDS_GET_RANGE_WEEKS(dataRangeIdx)}
                  height={420}
                />
              </section>

              {/* Campaign Planner (copy from Needs tab) */}
              <section className="bg-white border rounded-xl shadow-sm p-0 overflow-hidden">
                <CampaignBuilder 
                  onOpenSources={() => setActiveTab('review')} 
                  zones={zones}
                  onZonesChange={setZones}
                />
              </section>

              {/* Inspector below */}
              <section className="bg-white border rounded-xl shadow-sm p-4">
                <DataInspector job={dataInspectorJob} />
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Compact Job Icons Bar ----
function JobIconsBar({
  jobs,
  colors,
  coverageData,
  selectedJob,
  onSelect,
  zones
}: {
  jobs: string[];
  colors: Record<string, string>;
  coverageData: { job: string; color: string; coverage: number }[];
  selectedJob?: string;
  onSelect: (job: string) => void;
  zones: { lowRed: number; lowYellow: number; highYellow: number; highRed: number };
}) {
  const covMap = useMemo(() => {
    const m = new Map<string, number>();
    coverageData.forEach(d => m.set(d.job, d.coverage));
    return m;
  }, [coverageData]);
  const shadeColor = (hex: string, amount = 0.2) => {
    // darken towards black by 'amount' (0..1)
    const h = hex.replace('#','');
    const bigint = parseInt(h.length === 3 ? h.split('').map(c=>c+c).join('') : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    const dr = Math.max(0, Math.min(255, Math.round(r * (1 - amount))));
    const dg = Math.max(0, Math.min(255, Math.round(g * (1 - amount))));
    const db = Math.max(0, Math.min(255, Math.round(b * (1 - amount))));
    const toHex = (v:number) => v.toString(16).padStart(2,'0');
    return `#${toHex(dr)}${toHex(dg)}${toHex(db)}`;
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-center gap-3 min-w-max py-1">
        {jobs.map((job) => {
          const coverage = covMap.get(job) ?? 0;
          const baseColor = colors[job] || '#64748B';
          const numColor =
            coverage <= zones.lowRed ? '#dc2830'
            : coverage < zones.lowYellow ? '#ca8a04'
            : coverage <= zones.highYellow ? '#16a34a'
            : coverage <= zones.highRed ? '#ca8a04'
            : '#dc2626';

          const size = 90; // larger circle so percentage fits comfortably
          // Thicken strokes and keep geometry within viewBox without clipping
          const innerSW = 9;   // inner ring stroke width
          const outerSW = 9;   // make outer overflow ring same thickness as inner
          const MARGIN = 8;    // margin to avoid clipping (>= outerSW/2)
          const rOuter = (size / 2) - MARGIN;                           // outer ring centerline radius
          // Choose rInner so the outer ring begins exactly at the outer edge of the inner ring (no overlap)
          const rInner = Math.max(0, rOuter - (innerSW + outerSW) / 2);
          const cInner = 2 * Math.PI * rInner;
          const cOuter = 2 * Math.PI * rOuter;
          const innerPct = Math.max(0, Math.min(100, coverage));
          const overflow = Math.max(0, coverage - 100);
          const outerPct = Math.max(0, Math.min(100, overflow));
          const selected = selectedJob === job;
          const outerShade = shadeColor(baseColor, 0.2);

          return (
            <button
              key={job}
              onClick={() => onSelect(job)}
              className={`relative inline-flex flex-col items-center justify-center rounded-md border px-2 py-1 ${selected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              style={{ width: 118 }}
              title={`${job}: ${coverage}% coverage`}
            >
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
                <circle cx={size/2} cy={size/2} r={rInner} fill="#ffffff" stroke="#e5e7eb" strokeWidth={innerSW - 1} />
                <circle
                  cx={size/2} cy={size/2} r={rInner}
                  fill="none" stroke={baseColor} strokeWidth={innerSW}
                  strokeDasharray={`${(innerPct/100)*cInner} ${cInner}`}
                  strokeLinecap="butt"
                  transform={`rotate(-90 ${size/2} ${size/2})`}
                />
                {overflow > 0 && (
                  <circle
                    cx={size/2} cy={size/2} r={rOuter}
                    // 20% shade of base color (darken by 20%), same thickness as inner
                    fill="none" stroke={outerShade} strokeWidth={outerSW}
                    strokeDasharray={`${(outerPct/100)*cOuter} ${cOuter}`}
                    strokeLinecap="butt"
                    transform={`rotate(-90 ${size/2} ${size/2})`}
                  />
                )}
                {/* Centered percentage label with % sign */}
                <text
                  x={size/2}
                  y={size/2 + 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={numColor}
                  fontWeight={700}
                  fontSize="19"
                  fontFamily="'Inter Tight','Arial Narrow','Helvetica Neue',Arial,sans-serif"
                  fontStretch="semi-condensed"
                  letterSpacing="-0.5px"
                >
                  {`${coverage}%`}
                </text>
              </svg>
              <span className="mt-0 leading-none text-[12px] text-gray-700">{job}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- Zones Config Panel ----
function ZonesConfig({
  zones,
  onChange
}: {
  zones: { lowRed: number; lowYellow: number; highYellow: number; highRed: number };
  onChange: (z: { lowRed: number; lowYellow: number; highYellow: number; highRed: number }) => void;
}) {
  const [draft, setDraft] = useState(zones);
  useEffect(() => { setDraft(zones); }, [zones]);

  const apply = () => {
    const z = { ...draft };
    z.lowRed = Math.max(0, Math.min(1000, z.lowRed));
    z.lowYellow = Math.max(z.lowRed, Math.min(1000, z.lowYellow));
    z.highYellow = Math.max(z.lowYellow, Math.min(1000, z.highYellow));
    z.highRed = Math.max(z.highYellow, Math.min(1000, z.highRed));
    onChange(z);
  };

  const inputCls = "w-20 px-2 py-1 border rounded text-sm text-right";

  return (
    <div className="mt-3 space-y-2">
      <div className="grid grid-cols-2 gap-2 items-center">
        <label className="text-xs text-gray-600">Red ≤</label>
        <input type="number" className={inputCls} value={draft.lowRed} onChange={e=>setDraft({...draft, lowRed: Number(e.target.value||0)})}/>
        <label className="text-xs text-gray-600">Yellow to</label>
        <input type="number" className={inputCls} value={draft.lowYellow} onChange={e=>setDraft({...draft, lowYellow: Number(e.target.value||0)})}/>
        <label className="text-xs text-gray-600">Green to</label>
        <input type="number" className={inputCls} value={draft.highYellow} onChange={e=>setDraft({...draft, highYellow: Number(e.target.value||0)})}/>
        <label className="text-xs text-gray-600">Yellow to</label>
        <input type="number" className={inputCls} value={draft.highRed} onChange={e=>setDraft({...draft, highRed: Number(e.target.value||0)})}/>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={apply} className="px-3 py-1.5 border rounded text-sm">Apply</button>
        <button onClick={() => setDraft(zones)} className="px-3 py-1.5 border rounded text-sm">Reset</button>
        <span className="text-xs text-gray-500 ml-2">
          Zones: Red ≤ {zones.lowRed} • Yellow &lt; {zones.lowYellow} • Green ≤ {zones.highYellow} • Yellow ≤ {zones.highRed} • Red &gt; {zones.highRed}
        </span>
      </div>
    </div>
  );
}

